import React, { useState } from "react"
import { Trans } from "react-i18next"
import { Button, Row, Table } from "reactstrap";
import toastr from "toastr";
import { post } from "helpers/api_helper";
import { toImageUrl } from "helpers/file_upload_helper";
import FormSectionTitle from "pages/Common/FormSectionTitle";
import Loader from "common/loader";

const MAX_UPLOAD_MB = 20;

// Older rows saved description as plain text (e.g. "nine");
// normalize everything to [{ icon, description }] so the table always gets an array.
export const toDescriptionItems = (value) => {
    let parsed = value;
    if (typeof value === 'string') {
        try { parsed = JSON.parse(value); } catch { parsed = value; }
    }
    if (Array.isArray(parsed)) {
        return parsed.map(d => ({ icon: d?.icon ?? '', description: d?.description ?? '' }));
    }
    if (typeof parsed === 'string' && parsed.trim()) return [{ icon: '', description: parsed }];
    return [];
};

// Drop rows the user left completely empty before saving.
export const serializeDescriptionItems = (items) =>
    JSON.stringify(items.filter(d => d.icon || d.description.trim()));

const Description = ({ items, onChange }) => {
    const [uploading, setUploading] = useState({});

    // onChange is a useState setter; functional updates keep async uploads from overwriting newer edits.
    const updateItem = (index, changes) => {
        onChange(prev => prev.map((item, i) => i === index ? { ...item, ...changes } : item));
    }

    const addItem = () => {
        onChange(prev => [...prev, { icon: '', description: '' }]);
    }

    const removeItem = (index) => {
        onChange(prev => prev.filter((_, i) => i !== index));
    }

    // Uploads inline instead of using UploadImage: that component renders a hidden
    // #imagePath input, which would collide with the main image lookup on submit.
    const handleIconUpload = (index, event) => {
        const file = event.target.files[0];
        if (!file) return;
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
            toastr.error(`File too large (max ${MAX_UPLOAD_MB} MB)`);
            event.target.value = '';
            return;
        }
        setUploading(prev => ({ ...prev, [index]: true }));
        post('general/upload-image', { file, type: 'vehicle-categories' }, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then((response) => {
                updateItem(index, { icon: response.data.path });
            })
            .catch((error) => {
                event.target.value = '';
                toastr.error(error?.response?.data?.message || 'Upload failed');
            })
            .finally(() => {
                setUploading(prev => ({ ...prev, [index]: false }));
            });
    }

    return (
        <React.Fragment>
            <Row>
                <FormSectionTitle state={{ title: 'Description' }}></FormSectionTitle>
                <Table className="table table-striped mb-0">
                    <thead>
                        <tr style={{ background: "#f5f5f5" }}>
                            <th style={{ width: "35%" }}><Trans>Icon</Trans></th>
                            <th><Trans>Text</Trans></th>
                            <th style={{ width: "1%" }}>#</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index}>
                                <td>
                                    <input className="form-control" type="file" accept="image/*" onChange={(e) => handleIconUpload(index, e)} />
                                    {uploading[index] ? <Loader></Loader> : null}
                                    {item.icon ? (<img src={toImageUrl(item.icon)} width={60} className="mt-2" style={{ padding: "5px", background: "#f5f5f5", borderRadius: "5px" }}></img>) : null}
                                </td>
                                <td>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        placeholder="Enter Description"
                                        value={item.description}
                                        onChange={(e) => updateItem(index, { description: e.target.value })}
                                    ></textarea>
                                </td>
                                <td><Button className="btn btn-sm" color="danger" type="button" onClick={() => removeItem(index)}><Trans>Remove</Trans></Button></td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <div className="mt-2">
                    <Button className="btn btn-sm" color="info" type="button" onClick={addItem}><Trans>Add Description</Trans></Button>
                </div>
            </Row>
        </React.Fragment>
    )
}

export default Description
